import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSortableData from '@/hooks/useSortableData';
import type { Route } from '../models/Route';
import type { RouteTableRowData } from '../types/routeTable';
import { buildRoutesTableColumns } from '../config/routesTableColumns';
import { enrichRouteTableRow } from '../utils/enrichRouteTableRow';
import RouteRowActions from '../components/RouteRowActions';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';

interface UseRoutesTableParams {
  routes: Route[];
  currentPage: number;
  commerceTableFilter?: { commerceName: string };
}

const useRoutesTable = ({
  routes,
  currentPage,
  commerceTableFilter,
}: UseRoutesTableParams) => {
  const navigate = useNavigate();
  const { routeBuilderBasePath } = useMonitoringIntegration();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const onToggleExpand = useCallback((routeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) {
        next.delete(routeId);
        return next;
      }
      next.add(routeId);
      return next;
    });
  }, []);

  const onEditRoute = useCallback(
    (route: RouteTableRowData) => {
      const params = new URLSearchParams({
        editRouteId: route.id,
        logisticOperatorId: route.logisticOperatorId ?? '',
      });
      navigate(`${routeBuilderBasePath}?${params.toString()}`);
    },
    [navigate, routeBuilderBasePath]
  );

  const columns = useMemo(
    () =>
      buildRoutesTableColumns({
        expandedIds,
        onToggleExpand,
        onEditRoute,
        commerceTableFilter,
      }),
    [expandedIds, onToggleExpand, onEditRoute, commerceTableFilter]
  );


  const enrichedRows = useMemo(
    () => routes.map((route) => enrichRouteTableRow(route)),
    [routes]
  );

  const { sortedData: rows, selectedSort, handleSort } = useSortableData({
    data: enrichedRows,
    currentPage,
  });

  const renderActions = useCallback(
    (row: RouteTableRowData) => <RouteRowActions route={row} />,
    []
  );

  return {
    columns,
    rows,
    selectedSort,
    handleSort,
    renderActions,
    expandedIds,
  };
};

export default useRoutesTable;
